import React, {Fragment, useEffect, useState} from 'react';
import {
    addToEnd,
    allToEnd,
    allToStart,
    droppable,
    droppable2,
    entities as initial,
    getItems,
    multiDragAwareReorder,
    multiSelectTo as multiSelect,
    selectedToEnd,
} from './dragAndDropHelper';
import {DragDropContext} from 'react-beautiful-dnd';
import Column from './column';
import {sortFunc} from "../../services/sort";
import './styles.scss'
import {Button, Divider, Icon, Popup} from "semantic-ui-react";


const DragAndDrop = ({left, right, t, search, onChange, columns}) => {
    let [entities, setEntities] = useState({
        ...initial,
        columns: {
            [droppable.id]: {
                ...droppable,
                ...(columns[0] || {}),
                items: sortFunc(left.map(x => ({id: x.displayNameKey, content: x})), t, 'id'),
            },
            [droppable2.id]: {
                ...droppable2,
                ...(columns[1] || {}),
                items: right.map(x => ({id: x.displayNameKey, content: x})),
            },
        },
    });
    let [selected, setSelected] = useState([]);
    let [draggingId, setDraggingId] = useState(null);

    useEffect(
        () => {
            setEntities({
                ...entities,
                columns: {
                    [droppable.id]: {
                        ...entities.columns[droppable.id],
                        items: sortFunc(
                            left.map(x => ({id: x.displayNameKey, content: x})),
                            t,
                            'id',
                        ),
                    },
                    [droppable2.id]: {
                        ...entities.columns[droppable2.id],
                        items: right.map(x => ({id: x.displayNameKey, content: x})),
                    },
                },
            });
        },
        [left, right],
    );

    useEffect(() => {
        window.addEventListener('click', onWindowClick);
        window.addEventListener('keydown', onWindowKeyDown);
        window.addEventListener('touchend', onWindowTouchEnd);

        return () => {
            window.removeEventListener('click', onWindowClick);
            window.removeEventListener('keydown', onWindowKeyDown);
            window.removeEventListener('touchend', onWindowTouchEnd);
        };
    }, []);

    const onWindowClick = event => {
        if (event.defaultPrevented) {
            return;
        }
        unselectAll();
    };

    const onWindowKeyDown = event => {
        if (event.defaultPrevented) {
            return;
        }

        if (event.key === 'Escape') {
            unselectAll();
        }
    };

    const onWindowTouchEnd = event => {
        if (event.defaultPrevented) {
            return;
        }
        unselectAll();
    };

    const onDragStart = start => {
        const id = start.draggableId;
        const selectedItem = selected.find(item => item.id === id);

        // if dragging an item that is not selected - unselect all items
        if (!selectedItem) {
            unselectAll();
        }

        setDraggingId(start.draggableId);
    };

    const onDragEnd = result => {
        const {source, destination} = result;

        if (!destination || result.reason === 'CANCEL') {
            setDraggingId(null);
            return;
        }

        const processed = multiDragAwareReorder({
            entities,
            selected,
            source,
            destination,
        });

        setEntities(processed.entities);
        setSelected(processed.selected);
        setDraggingId(null);

        onChange(processed.entities.columns[droppable2.id].items.map(x => x.content));
    };

    const toggleSelection = item => {
        const wasSelected = selected.map(item => item.id).includes(item.id);

        const newSelected = (() => {
            // Task was not previously selected
            // now will be the only selected item
            if (!wasSelected) {
                return [item];
            }

            // Task was part of a selected group
            // will now become the only selected item
            if (selected.length > 1) {
                return [item];
            }

            // task was previously selected but not in a group
            // we will now clear the selection
            return [];
        })();

        setSelected(newSelected);
    };

    const toggleSelectionInGroup = item => {
        const index = selected.map(item => item.id).indexOf(item.id);

        // if not selected - add it to the selected items
        if (index === -1) {
            setSelected([...selected, item]);
            return;
        }

        // it was previously selected and now needs to be removed from the group
        const shallow = [...selected];
        shallow.splice(index, 1);
        setSelected(shallow);
    };

    const multiSelectTo = item => {
        const updated = multiSelect(entities, selected, item, search, t);

        if (updated == null) {
            return;
        }

        setSelected(updated);
    };

    const doubleClick = (item, columnId, index) => {
        const processed = addToEnd(item, entities, columnId, index);

        setEntities(processed.entities);

        onChange(processed.entities.columns[droppable2.id].items.map(x => x.content));
    };

    const applyChange = (processed, clearSelection = false) => {
        setEntities(processed.entities);
        if (clearSelection) {
            setSelected([]);
        }
        onChange(processed.entities.columns[droppable2.id].items.map(x => x.content));
    };

    const clickSelectedToRight = (event) => {
        event.stopPropagation();
        const processed = selectedToEnd(entities, droppable.id, selected);
        applyChange(processed, true);
    };

    const clickAllToRight = (event) => {
        event.stopPropagation();
        const processed = allToEnd(entities, droppable.id);
        applyChange(processed, true);
    };

    const clickSelectedToLeft = (event) => {
        event.stopPropagation();
        const processed = selectedToEnd(entities, droppable2.id, selected);
        applyChange(processed, true);
    };

    const clickAllToLeft = (event) => {
        event.stopPropagation();
        const processed = allToStart(entities, droppable2.id);
        applyChange(processed, true);
    };

    const unselectAll = () => {
        setSelected([]);
    };

    const leftItems = getItems(entities, droppable.id);
    const rightItems = getItems(entities, droppable2.id);
    const selectedIds = selected.map(item => item.id);
    const hasSelectedInLeft = leftItems.some(item => selectedIds.includes(item.id));
    const hasSelectedInRight = rightItems.some(item => selectedIds.includes(item.id));

    const stylePopup = {
        fontWeight: 700,
        fontSize: '12px',
        color: '#475566'
    };

    const transferButtons = [
        {
            icon: 'angle double right',
            content: 'Добавить все поля в отчет',
            onClick: clickAllToRight,
            disabled: !leftItems.length,
        },
        {
            icon: 'angle right',
            content: 'Добавить выбранное поле в отчет (Ctrl+Click для выбора нескольких)',
            onClick: clickSelectedToRight,
            disabled: !hasSelectedInLeft,
        },
        {
            icon: 'angle left',
            content: 'Убрать выбранное поле из отчета (Ctrl+Click для выбора нескольких)',
            onClick: clickSelectedToLeft,
            disabled: !hasSelectedInRight,
        },
        {
            icon: 'angle double left',
            content: 'Убрать все поля из отчета',
            onClick: clickAllToLeft,
            disabled: !rightItems.length,
        },
    ];

    return (
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            {entities.columnOrder.map((columnId, index) => (
                <Fragment key={index}>
                    <Column
                        index={index}
                        column={entities.columns[columnId]}
                        items={getItems(entities, columnId)}
                        selected={selected}
                        key={columnId}
                        t={t}
                        search={search}
                        draggingId={draggingId}
                        toggleSelection={toggleSelection}
                        toggleSelectionInGroup={toggleSelectionInGroup}
                        multiSelectTo={multiSelectTo}
                        doubleClick={doubleClick}
                    />
                    {index === 0 && <Divider className='drag-and-drop__transfer-btns' vertical>
                        <div className='drag-and-drop__transfer-btns-group'>
                            {transferButtons.map((btn, btnIndex) => (
                                <Popup
                                    key={btnIndex}
                                    trigger={
                                        <Button
                                            className='modal__actions_yes'
                                            disabled={btn.disabled}
                                            onClick={btn.onClick}
                                        >
                                            <Icon name={btn.icon} />
                                        </Button>
                                    }
                                    content={btn.content}
                                    position='bottom left'
                                    offset={[-7]}
                                    style={stylePopup}
                                />
                            ))}
                        </div>
                    </Divider>}
                </Fragment>
            ))}
        </DragDropContext>
    );
};

export default DragAndDrop;



// WEBPACK FOOTER //
// ./App/components/DragAndDrop/index.jsx